<template>
    <div>
        <div v-show="error" class="alert alert-danger">
            <p v-html="error" />
        </div>

        <div class="row">
            <div class="col-md-6 login-form">
                <form id="form" method="POST" novalidate role="form" @submit="submit">
                    <div class="mb-4" :class="{ 'has-error': usernameError }">
                        <label for="sign-in-user-name-input" class="block mb-1 font-bold">{{
                            $t("SignIn.UserNameLabelText")
                        }}</label>
                        <input
                            id="sign-in-user-name-input"
                            v-model="username"
                            type="text"
                            name="UserName"
                            class="py-1 rounded-md w-3/4" />
                        <span class="text-danger">{{ usernameError }}</span>
                    </div>

                    <div class="mb-4" :class="{ 'has-error': passwordError }">
                        <label for="sign-in-password-input" class="block mb-1 font-bold">{{
                            $t("Global.PasswordLabel")
                        }}</label>
                        <input
                            id="sign-in-password-input"
                            v-model="password"
                            type="password"
                            name="Password"
                            class="py-1 rounded-md w-3/4" />
                        <span class="text-danger">{{ passwordError }}</span>
                        <p class="mt-2">
                            <a href="/forgot-password">{{ $t("SignIn.ForgotPasswordText") }}</a>
                        </p>
                    </div>

                    <button id="sign-in-button" type="submit" :disabled="loading" class="btn btn-bg-primary">
                        <span v-show="!loading">{{ $t("SignIn.LoginButtonText") }}</span>
                        <span v-show="loading">{{ $t("SignIn.LoginButtonLoading") }}</span>
                    </button>
                </form>

                <p class="mt-1">
                    {{ $t("SignIn.CreateAccount") }} <a href="/register">{{ $t("SignIn.SignupLink") }}</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
const { t } = useI18n();
const localePath = useLangPath();

const props = defineProps({
    cookie: { type: String, default: null },
    loginUrl: { type: String, required: true },
});

const username = ref(null);
const password = ref("");
const usernameError = ref("");
const passwordError = ref("");
const error = ref("");
const loading = ref(false);

function validate() {
    usernameError.value = "";
    passwordError.value = "";

    if (!username.value) {
        usernameError.value = t("SignIn.UserNameRequiredErrorMessage");
    }
    if (!password.value) {
        passwordError.value = t("Global.PasswordRequired");
    }

    return !(usernameError.value || passwordError.value);
}

async function submit(event) {
    event.preventDefault();
    event.stopPropagation();

    // check to see if they had an anonymous bom, if so lets save it to their account on login.
    let bomSearch = BomHelpers.getAnonBOM();
    if (bomSearch) {
        // trim json
        delete bomSearch.PossibleDistributors;

        if (bomSearch.Parts && bomSearch.Parts.length) {
            for (const part of bomSearch.Parts) {
                delete part.DistributorResults;
                delete part.Id;
                delete part.Key;
                delete part.ManufacturerId;
                delete part.ManufacturerName;
                delete part.Notification;
                delete part.PartDetail;
                delete part.Exists;
                delete part.PossibleManufacturers;
            }
        } else {
            bomSearch = null;
        }
    }

    if (validate()) {
        loading.value = true;

        try {
            const loginResponse = await axios.post(props.loginUrl, {
                UserName: username.value,
                Password: password.value,
            });

            if (loginResponse.status === 200) {
                if (PageHelpers.GetCookie(CONTINUE_AS_GUEST_FLAG)) {
                    // delete the cookie if it exists by setting its expired date to the past
                    const expiry = new Date();
                    expiry.setFullYear(1969);
                    PageHelpers.SetCookie(CONTINUE_AS_GUEST_FLAG, "false", expiry, "/");
                }

                const expiry = new Date();
                expiry.setMinutes(expiry.getMinutes() + 15);

                if (bomSearch) {
                    try {
                        const bomResponse = await axios.post("/api/bom", bomSearch);

                        if (bomResponse.status === 200) {
                            // make sure to remove the anon bom from local storage since we just saved it to their account.
                            localStorage.removeItem("ecia-bom-anonymous");
                        }
                    } catch (error) {
                        // don't let this failure stop the log in, we'll just try again next time they log in
                        console.error(error);
                    }

                    loading.value = false;
                    window.location = loginResponse.data.ReturnUrl;
                    if (props.cookie) {
                        PageHelpers.SetCookie(props.cookie, "true", expiry, "/");
                    }
                } else {
                    if (props.cookie) {
                        PageHelpers.SetCookie(props.cookie, "true", expiry, "/");
                    }
                    await navigateTo(localePath(loginResponse.data.ReturnUrl));
                }
            } else {
                error.value = t("Global.GenericError");
                loading.value = false;
            }
        } catch (error) {
            const errorResponse = error && error.response && error.response.data && error.response.data.Error;
            error.value = errorResponse || t("Global.GenericError");
            loading.value = false;
        }

        if (!bomSearch) {
            loading.value = false;
        }
    }
}
</script>
